

    import "splitpanes/dist/splitpanes.css";

    import Vue from "vue";
    import Component from "vue-class-component";
    import { Prop, Watch } from "vue-property-decorator";

    import draggable from "vuedraggable";
    import Utils from "@/utilities/Utils";
    import { mapData } from "@/utilities/DataMapping";
    import ApiClient from "@/utilities/ApiClient";
    import ApiButton from "@/vue/components/ApiButton.vue";
    import PrimaryButton from "@/vue/components/PrimaryButton.vue";
    import PageDialogue from "@/vue/components/PageDialogue.vue";
    import TextPart from "@/vue/components/PageParts/TextPart.vue";
    import NotePart from "@/vue/components/PageParts/NotePart.vue";
    import MapPartLeaflet from "@/vue/components/PageParts/MapPartLeaflet.vue";
    import MapPartGoogle from "@/vue/components/PageParts/MapPartGoogle.vue";
    import ResourceAndTextPart from "@/vue/components/PageParts/ResourceAndTextPart.vue";
    import RadioChoicePart from "@/vue/components/PageParts/RadioChoicePart.vue";
    import CheckboxChoicesPart from "@/vue/components/PageParts/CheckboxChoicesPart.vue";
    import FreeTextPart from "@/vue/components/PageParts/FreeTextPart.vue";
    import DownloadPart from "@/vue/components/PageParts/DownloadPart.vue";
    import PhotoCapturePart from "@/vue/components/PageParts/PhotoCapturePart.vue";
    import ConfirmToContinuePart from "@/vue/components/PageParts/ConfirmToContinuePart.vue";
    import PasscodeToContinuePart from "@/vue/components/PageParts/PasscodeToContinuePart.vue";
    import PagePartDialogue from "@/vue/components/PagePartDialogue.vue";
    import ConfirmDialogue from "@/vue/components/ConfirmDialogue.vue";
    import { PagePartSearchParameters } from "@/model/PagePartSearchParameters";

    import { Site } from "@/model/Site";
    import { Client } from "@/model/Client";
    import { Briefing } from "@/model/Briefing";
    import { IPage, Page } from "@/model/Page";
    import { IPagePart, PagePart } from "@/model/PagePart";
    import { IResource, Resource } from "@/model/Resource";
    import { VisitAnswer } from "@/model/VisitAnswer";
    import { IMapplicOption, MapplicOption } from "@/model/MapplicOption";
    import { IPagePartOptions } from "@/model/pagePartOptions/IPagePartOptions";
    import { ISaveResponse } from "@/model/ISaveResponse";

    @Component({ components: {
            draggable,
            ApiButton,
            PrimaryButton,
            PageDialogue,
            TextPart,
            NotePart,
            MapPartLeaflet,
            MapPartGoogle,
            ResourceAndTextPart,
            RadioChoicePart,
            CheckboxChoicesPart,
            FreeTextPart,
            DownloadPart,
            PhotoCapturePart,
            ConfirmToContinuePart,
            PasscodeToContinuePart,
            PagePartDialogue,
            ConfirmDialogue
        }})
    export default class BriefingPage extends Vue {

        //
        // -- properties
        //

        @Prop({ default: 176 })
        positionTop!: number;

        @Prop({ required: true })
        briefing!: Briefing;

        @Prop({ required: true })
        site!: Site;

        @Prop({ required: true })
        client!: Client;

        // empty string means no-action, empty GUID means add new
        @Prop({ default: "" })
        pageID!: string;

        @Prop({ required: true })
        mapplicOptionList!: Array<MapplicOption>;

        get isNoAction() {
            return !this.pageID;
        }

        page: Page = new Page();

        pagePartList: Array<PagePart> = [];

        isDragging: boolean = false;

        //
        // -- watchers and handlers
        //

        @Watch("pageID", { immediate: false })
        async onPageChanged(val: any, old: any): Promise<void> {
            // console.log(`~~~~ Page ID changed - this.pageID=${this.pageID} old:${old}`);
            // console.trace();
            // this.reset("watch page ID"); -- NOOO reset breaks the page add!
            if(!Utils.isEmptyId(this.pageID)) {
                await this.loadPage(this.pageID);
                await this.loadPagePartList(this.pageID);
            }
        }

        // bubble up to Briefing component
        onPageSaved(): void {
            this.$emit("page-saved", this.page.id);
        }

        onResetPage(): void {
            Utils.resetObject(this.page);
            this.page.briefingID = this.briefing.id;
            // can't change PageID here becuase it's a prop - so bubble it up and do it in Briefing.vue
            this.$emit("page-reset", this.page.id);
        }

        //
        // -- methods
        //

        // failed attempt to fix map-dragging nastiness - with...  :style="mapDragStopStyle(part)"
        // mapDragStopStyle(part: PagePart): Record<string, string> {
        //     return part.component === PagePartComponent.Map
        //         ? { pointerEvents: "none" }
        //         : { pointerEvents: "auto" };
        // }

        reset(): void {
            Utils.resetObject(this.page);
            this.page.briefingID = this.briefing.id;
            this.pagePartList = [];
        }

        newPage(isQuiz: boolean, sectionID: string, displayOrder: number): void {
            this.reset();
            this.page.isQuiz = isQuiz;
            this.page.briefingID = this.briefing.id;
            this.page.displayOrder = displayOrder;
            this.page.sectionID = sectionID;

console.log("+++ briefingpage newPage ", this.page);

            this.editPage();
        }

        async loadPage(pageID: string): Promise<void> {
            const data: IPage = await ApiClient.get(`/api/page/load?id=${pageID}`);
            this.page.update(data);
        }

        async loadPagePartList(pageID: string): Promise<void> {
            const mappingOptions = { root: (data: IPagePart) => new PagePart(data) };
            const parameters = new PagePartSearchParameters();
            parameters.pageID = pageID;
            const data: Array<IPagePart> = await ApiClient.post("/api/pagePart/search", parameters);
            this.pagePartList = mapData<Array<PagePart>>(data, mappingOptions);
        }

        //
        // --
        //

        answerForPart(pagePartID: string): VisitAnswer {
            // just return a new, empty answer as a dummy
            return new VisitAnswer(VisitAnswer.create(Utils.emptyGuidValue, pagePartID));
        }

        confirmClonePage() {
            const dialog: ConfirmDialogue = this.$refs.confirmCloneDialogue as ConfirmDialogue;
            dialog.show();
        }

        async doClonePage() {
            const response: ISaveResponse = await ApiClient.get(`/api/page/clone?briefingID=${this.briefing.id}&sourcePageID=${this.page.id}`, "page-clone");
            this.$emit("page-saved", response.newID);
        }

        editPage(): void {
            const dialog: PageDialogue = this.$refs.pageDialogue as PageDialogue;
            dialog.show();
        }

        addPagePart(): void {
            const dialogue = this.$refs.pagePartDialogue as PagePartDialogue;
            dialogue.add();
        }

        editPagePart(pagePart: PagePart): void {
            const dialogue: PagePartDialogue = this.$refs.pagePartDialogue as PagePartDialogue;
            dialogue.edit(pagePart.id);
        }

        onPagePartSaved(pagePart: PagePart): void {
            console.log("page part saved ", pagePart);
        }

        // should be change event, not end!
        onDragEnd(): void {
            const idList = this.pagePartList.map(p => p.id);
            this.reorderPageParts(idList);
        }

        checkMove(event: any): boolean {
            // maybe we can stop dragging map elements with this? - no, we can't.
            return true;
        }

        async reorderPageParts(idList: Array<string>): Promise<void> {
            await ApiClient.post("/api/pagePart/reorder", idList);
        }
    }
